import { createFileRoute, Link } from "@tanstack/react-router";
import { Heart, Factory, Truck, ScanLine } from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { Card, CardContent } from "@/components/ui/card";
import { TableScroll } from "@/components/layout/ContentBlock";
import { FaqSection } from "@/components/seo/FaqSection";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, softwareAppJsonLd, breadcrumbJsonLd, ldScript } from "@/lib/seo";
import { ScannerApp } from "@/components/scanner/ScannerApp";

const PATH = "/data-matrix-reader";
const TITLE = "Data Matrix Reader — Scan & Decode Data Matrix Codes Online";
const DESC =
  "Free Data Matrix reader. Scan with your camera or upload a Data Matrix image to decode healthcare, manufacturing and GS1 codes — all in your browser.";

const FAQ = [
  {
    q: "What's the difference between Data Matrix and QR code?",
    a: "Both are 2D matrix barcodes, but Data Matrix is denser per square millimeter and is the default for very small marks — pharmaceutical blister packs, electronics components, and direct-part-marked metal. QR codes are tuned for fast camera scanning at a distance, which is why they dominate consumer-facing uses like posters and Wi-Fi sharing.",
  },
  {
    q: "Can this reader decode GS1 DataMatrix?",
    a: "Yes. GS1 DataMatrix is a Data Matrix code that carries GS1 Application Identifiers (AIs) such as (01) GTIN, (10) batch, (17) expiry, (21) serial. This reader returns the raw decoded payload. To split it into individual AIs, paste the result into the GS1 Digital Link Decoder.",
  },
  {
    q: "Why won't my direct-part-marked Data Matrix scan?",
    a: "DPM codes etched into metal often need controlled lighting (diffuse or dome) to produce enough contrast. The reader uses your phone or webcam image as-is, so a flat, evenly lit, high-contrast photo decodes far more reliably than a glare-heavy snapshot.",
  },
  {
    q: "Is it safe to scan pharmaceutical Data Matrix codes here?",
    a: "Decoding runs entirely in your browser via the JavaScript port of ZXing. The image is never uploaded and the decoded value isn't logged or stored — useful when scanning serialized medicine codes that could contain a unique batch and serial.",
  },
  {
    q: "Does the Data Matrix reader support rectangular (DMRE) codes?",
    a: "It decodes all square sizes and the six rectangular sizes, from 8 × 18 to 16 × 48 modules, defined in ISO/IEC 16022. The extra rectangular sizes added by DMRE (ISO/IEC 21471) are not in the decoder's size table, so a DMRE code will usually not decode here.",
  },
  {
    q: "Can I scan a Data Matrix from a screen?",
    a: "Yes, but reduce screen brightness slightly to limit glare and keep the code sharp in the frame. If the camera struggles, screenshot the code and use the image upload option instead.",
  },
  {
    q: "Can I Read a Data Matrix Code With a Phone App?",
    a: "You can, but this page already works in your phone's browser with no app: open the Camera tab, allow access and the rear camera is used by default. The decoded text can be copied straight from the result panel into another app.",
  },
  {
    q: "Is There a Free Data Matrix Reader Online?",
    a: "Yes, this one is free and needs no account. It reads from the camera, an uploaded image or an image URL, and decoding happens in your browser, so photos of serialized packs are not sent to a server.",
  },
  {
    q: "How Do You Read a Data Matrix Barcode?",
    a: "Use a camera-based reader such as a phone, a webcam or an imaging scanner; single-line laser scanners cannot read 2D codes. The decoder locates the L-shaped border, samples each module and applies Reed-Solomon error correction to recover the data. On this page, open the Camera tab or upload a photo and the text appears in the result panel.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Readers", path: "/readers" },
  { name: "Data Matrix Reader", path: PATH },
];

export const Route = createFileRoute("/data-matrix-reader")({
  component: DataMatrixReaderPage,
  head: () => {
    const base = pageHead({
      title: TITLE,
      description: DESC,
      path: PATH,
    });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode Data Matrix Reader",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

function DataMatrixReaderPage() {
  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="Data Matrix Reader"
      description="Scan a Data Matrix code with your camera or upload an image. Built for healthcare, manufacturing, logistics, and GS1 DataMatrix payloads — decoded locally in your browser. You can also paste the URL of an online image, and the result flags any code that decodes as a format other than Data Matrix."
      crumbs={CRUMBS}
      content={
        <>
          <DataMatrixVsQr />
          <ToolSeoSections
            aboutHeading="About this Data Matrix reader"
            about={
              <>
                <p>
                  The ReadBarcode Data Matrix reader decodes 2D Data Matrix codes — including GS1
                  DataMatrix — directly in your browser. Use your phone camera, your webcam, or upload
                  an image file. There's no app to install, no signup, and no upload step: the image
                  stays on your device and is processed locally with the JavaScript port of ZXing.
                </p>
                <p>
                  Data Matrix is the workhorse 2D symbology for healthcare, manufacturing, and
                  logistics, where space is tight and codes must survive being marked directly onto
                  parts. This online Data Matrix reader is built specifically for those workflows.
                </p>
              </>
            }
            whenToUse={[
              "Reading a GS1 DataMatrix on a pharmaceutical pack to confirm the GTIN, batch, expiry and serial.",
              "Decoding a direct-part-marked Data Matrix on an electronics component or aerospace part.",
              "Scanning a unit-load logistics label that carries a Data Matrix instead of a Code 128.",
              "Auditing what a freshly generated Data Matrix actually encodes before mass production.",
              "Quickly checking a small Data Matrix on a coupon, ticket, or appointment slip.",
            ]}
            howItWorks={[
              {
                title: "Pick an input",
                desc: "Choose the camera, an uploaded image, or paste a URL to an image of the Data Matrix code.",
              },
              {
                title: "Frame the code",
                desc: "Fill the frame with the Data Matrix square. Even lighting beats high resolution every time.",
              },
              {
                title: "Auto-decode",
                desc: "The ZXing engine detects the L-shaped finder pattern and reads the modules.",
              },
              {
                title: "Inspect the payload",
                desc: "The result panel shows the detected format (Data Matrix) and the decoded text.",
              },
              {
                title: "Parse further if needed",
                desc: "For GS1 DataMatrix payloads, pass the value to the GS1 Digital Link Decoder to split it into AIs.",
              },
            ]}
            whatToAvoid={[
              "Scanning glossy direct-part marks with a single point light source — the glare wipes out modules.",
              "Digital-zooming on a small Data Matrix; move closer instead so each module stays crisp.",
              "Cropping away the quiet zone around the code — most failures are quiet-zone failures.",
              "Expecting product names or batch metadata — this is a decoder, not a healthcare database.",
              "Confusing a tiny QR code for a Data Matrix. QR has three corner finder squares; Data Matrix has one solid L.",
            ]}
            tips={[
              "Diffuse, indirect light gives the best contrast on metal or foil packaging.",
              "For very small codes, take the photo first at maximum resolution, then upload it here.",
              "If the result starts with the GS1 FNC1 indicator, treat it as GS1 DataMatrix and parse the AIs.",
              "When a code refuses to scan, try rotating the image 90° — Data Matrix is orientation-agnostic but the camera autofocus isn't.",
              "Use the same tool to audit suppliers' Data Matrix codes before integrating them into your packing line.",
            ]}
          />
          <DataMatrixBasics />
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <ScannerApp
        expectedFormat="Data Matrix"
        cameraHint="Point your camera at a Data Matrix code — the small square pattern, not a QR code."
        emptyHint="Scan or upload a Data Matrix code. The decoded value and detected format will appear here."
      />
      <UseCases />
    </ToolLayout>
  );
}

function UseCases() {
  const cases = [
    {
      icon: Heart,
      title: "Healthcare",
      desc: "GS1 DataMatrix on pharmaceutical packs encodes GTIN, batch, expiry and unique serial — required by EU FMD and US DSCSA traceability rules.",
    },
    {
      icon: Factory,
      title: "Manufacturing",
      desc: "Direct-part-marked Data Matrix on castings, PCBs and machined parts survives heat, oil and abrasion that would destroy a printed label.",
    },
    {
      icon: Truck,
      title: "Logistics",
      desc: "Compact Data Matrix labels fit on small parcels and components where a Code 128 wouldn't, while still carrying full SSCC and lot data.",
    },
    {
      icon: ScanLine,
      title: "GS1 DataMatrix",
      desc: "Standardized Data Matrix profile carrying GS1 Application Identifiers, used across retail, healthcare and supply chain.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-2xl font-semibold tracking-tight">Where Data Matrix is used</h2>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Data Matrix dominates wherever the code has to be tiny, durable, and machine-readable. These
        are the four use cases this reader is tuned for.
      </p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {cases.map((c) => (
          <Card key={c.title}>
            <CardContent className="p-4">
              <c.icon className="h-5 w-5 text-primary" aria-hidden />
              <div className="mt-2 text-sm font-semibold">{c.title}</div>
              <p className="mt-1 text-xs text-muted-foreground">{c.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

function DataMatrixVsQr() {
  const rows = [
    {
      feature: "Finder pattern",
      dm: "Solid L on two sides + timing on the other two",
      qr: "Three corner squares (and an alignment pattern)",
    },
    {
      feature: "Minimum practical size",
      dm: "Roughly 2 × 2 mm with industrial print",
      qr: "Roughly 10 × 10 mm for camera scanning",
    },
    {
      feature: "Typical payload",
      dm: "Up to ~2,300 alphanumeric characters",
      qr: "Up to ~4,300 alphanumeric characters",
    },
    {
      feature: "Common in",
      dm: "Healthcare, manufacturing, aerospace, logistics",
      qr: "Marketing, payments, Wi-Fi, contacts",
    },
    {
      feature: "Direct part marking",
      dm: "Yes — laser/dot-peen friendly",
      qr: "Rarely, and at larger sizes",
    },
  ];
  return (
    <>
      <h2>Data Matrix vs QR code</h2>
      <p>
        Both are 2D codes, but they're built for different jobs. Use this quick comparison to
        confirm you're scanning the right thing.
      </p>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Feature</th>
              <th scope="col">Data Matrix</th>
              <th scope="col">QR Code</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.feature}>
                <td>{r.feature}</td>
                <td>{r.dm}</td>
                <td>{r.qr}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </>
  );
}

function DataMatrixBasics() {
  return (
    <>
      <h2>What Is a Data Matrix Code?</h2>
      <p>A Data Matrix code is a square or rectangular 2D barcode framed by a solid L-shaped border on two sides and an alternating clock pattern on the other two. It is defined in ISO/IEC 16022, and the ECC 200 version used today recovers damaged data with Reed-Solomon error correction.</p>
      <p>Square symbols run from 10 × 10 to 144 × 144 modules and rectangular ones from 8 × 18 to 16 × 48; this reader decodes all of those sizes. In manufacturing the code is often called a DMC, so a DMC code reader online is the same tool.</p>
      <h3>GS1 DataMatrix vs Plain Data Matrix</h3>
      <p>A GS1 DataMatrix is an ordinary ECC 200 symbol whose data begins with FNC1, which signals that GS1 Application Identifiers follow. A plain Data Matrix holds any text, such as a part number or a web address, and that includes a GS1 Digital Link URL.</p>
      <p>In this reader's result, each FNC1 appears as the invisible group separator character (ASCII 29). Paste the value into the <Link to="/gs1-digital-link-decoder">GS1 Digital Link decoder</Link> to split it into GTIN, expiry, batch and serial.</p>
      <h2>How to Read a Data Matrix Code</h2>
      <p>To read a Data Matrix code, open the Camera tab and hold the code flat in the middle of the frame, or upload a photo in the Image tab. The text and the detected format appear as soon as the code is found.</p>
      <ol>
        <li>Press Start camera and allow access; scanning stops at the first code read.</li>
        <li>Keep the blank margin around the L-shaped border in view.</li>
        <li>Check the format label, then press Copy value.</li>
      </ol>
      <p>If the phone cannot focus on a tiny code, photograph it with the camera app and use this datamatrix scanner as a Data Matrix reader from image files. The Image tab runs the same decoder on the saved photo.</p>
      <h3>Data Matrix Barcode on Medicine and Parts</h3>
      <p>On a medicine pack it is normally a GS1 DataMatrix carrying (01) GTIN, (17) expiry date as YYMMDD, (10) batch and (21) serial number. On parts it is marked into the surface by laser or dot peen, so photograph it square-on under diffuse light. To rehearse a workflow before real codes arrive, make a sample with the <Link to="/data-matrix-generator">Data Matrix generator</Link> and read it back here.</p>
    </>
  );
}

function RelatedTools() {
  const related = [
    {
      path: "/gs1-digital-link-decoder",
      label: "GS1 Digital Link Decoder",
      desc: "Split a GS1 DataMatrix payload into GTIN, batch, expiry and serial.",
    },
    {
      path: "/qr-code-reader",
      label: "QR Code Reader",
      desc: "Scan QR codes — the other major 2D symbology.",
    },
    {
      path: "/barcode-reader",
      label: "Online Barcode Reader",
      desc: "Multi-format scanner that handles 1D and 2D codes side-by-side.",
    },
    {
      path: "/read-barcode-from-image",
      label: "Read Barcode From Image",
      desc: "Drop in a photo to decode any supported barcode, including Data Matrix.",
    },
    {
      path: "/gtin-validator",
      label: "GTIN Validator",
      desc: "Verify the GTIN extracted from a GS1 DataMatrix payload.",
    },
    {
      path: "/barcode-type-detector",
      label: "Barcode Type Detector",
      desc: "Identify whether a number is GTIN, ISBN, UPC or EAN before scanning.",
    },
    {
      path: "/data-matrix-generator",
      label: "Data Matrix Generator",
      desc: "Create a Data Matrix code to test your scanning workflow.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-2xl font-semibold tracking-tight">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-semibold group-hover:text-primary">{r.label}</div>
            <div className="mt-1 text-sm text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}
